<template>
  <div
    class="w-full p-6 bg-white shadow-sm hover:shadow-md transition-shadow rounded-lg border border-gray-100 flex flex-col justify-between gap-4 overflow-hidden"
  >
    <div class="flex flex-col gap-4">
      <div class="flex justify-between items-start">
        <div
          class="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
        >
          <img
            v-if="organisation.logo_url"
            :src="organisation.logo_url"
            alt="Logo"
            class="object-cover w-full h-full"
          />
          <span v-else class="text-gray-400 text-xs">
            <Translation name="card.no_logo" default="No Logo" />
          </span>
        </div>

        <div class="text-right">
          <p class="text-gray-400 text-xs uppercase tracking-wider">
            <Translation name="card.updated" default="Updated" />
          </p>
          <p class="text-gray-600 text-sm font-semibold">{{ formattedDate }}</p>
        </div>
      </div>

      <div>
        <h3 class="text-gray-900 text-lg font-semibold mb-3 truncate">
          {{ organisation.name }}
        </h3>

        <div class="relative w-full min-h-[28px] mb-3" ref="sectorsWrapper">
          <div
            ref="sectorsMeasurer"
            class="absolute top-0 left-0 flex gap-2 opacity-0 pointer-events-none w-max z-[-1]"
          >
            <span class="px-3 py-1 bg-white border border-gray-200 text-gray-700 text-sm rounded-full whitespace-nowrap shrink-0">
              {{ organisation.organisation_type }}
            </span>
            <span
              v-for="sector in organisation.sectors || []"
              :key="`measure-${sector}`"
              class="px-3 py-1 bg-white border border-gray-200 text-gray-700 text-sm rounded-full whitespace-nowrap shrink-0"
            >
              {{ sector }}
            </span>
          </div>

          <div class="flex flex-nowrap gap-2 w-full overflow-hidden items-center">
            <span class="px-3 py-1 bg-white border border-gray-200 text-gray-700 text-sm rounded-full whitespace-nowrap shrink-0">
              {{ organisation.organisation_type }}
            </span>
            
            <span
              v-for="sector in (organisation.sectors || []).slice(0, visibleSectorsCount)"
              :key="sector"
              class="px-3 py-1 bg-white border border-gray-200 text-gray-700 text-sm rounded-full whitespace-nowrap shrink-0"
            >
              {{ sector }}
            </span>
            
            <div
              v-if="(organisation.sectors?.length || 0) > visibleSectorsCount"
              class="inline-flex items-center justify-start gap-1 pt-[2px] pb-[2px] pl-[3px] pr-[8px] bg-gray-50 rounded-2xl shrink-0"
            >
              <div class="text-center text-gray-700 text-[14px] font-normal leading-[20px] break-words">
                +{{ organisation.sectors.length - visibleSectorsCount }}
              </div>
            </div>
          </div>
        </div>

        <div class="relative w-full min-h-[28px]" ref="countriesWrapper">
          <div
            ref="countriesMeasurer"
            class="absolute top-0 left-0 flex gap-2 opacity-0 pointer-events-none w-max z-[-1]"
          >
            <span
              v-for="(country, index) in organisation.countries || []"
              :key="`measure-${country}`"
              class="px-3 py-1 bg-gray-50 border border-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <span
                v-if="organisation.countries_codes && organisation.countries_codes[index]"
                :class="`fi fi-${organisation.countries_codes[index].toLowerCase()}`"
                class="rounded-sm overflow-hidden shrink-0"
                style="font-size: 16px"
              ></span>
              {{ country }}
            </span>
          </div>

          <div class="flex flex-nowrap gap-2 w-full overflow-hidden items-center">
            <span
              v-for="(country, index) in (organisation.countries || []).slice(0, visibleCountriesCount)"
              :key="country"
              class="px-3 py-1 bg-gray-50 border border-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <span
                v-if="organisation.countries_codes && organisation.countries_codes[index]"
                :class="`fi fi-${organisation.countries_codes[index].toLowerCase()}`"
                class="w-4 h-4 shrink-0 overflow-hidden bg-cover bg-center"
              ></span>
              {{ country }}
            </span>
            
            <div
              v-if="(organisation.countries?.length || 0) > visibleCountriesCount"
              class="inline-flex items-center justify-start gap-1 pt-[2px] pb-[2px] pl-[3px] pr-[8px] bg-gray-50 rounded-2xl shrink-0"
            >
              <div class="text-center text-gray-700 text-[14px] font-normal leading-[20px] break-words">
                +{{ organisation.countries.length - visibleCountriesCount }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-4 border-t border-gray-100 flex justify-end">
      <button
        @click="isModalOpen = true"
        class="px-6 py-2 bg-emerald-50 hover:bg-emerald-100 text-teal-900 font-semibold rounded-lg transition-colors cursor-pointer"
      >
        <Translation name="card.view" default="View" />
      </button>
    </div>

    <OrganisationModal
      :organisation="organisation"
      :is-open="isModalOpen"
      @close="isModalOpen = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import OrganisationModal from "./OrganisationModal.vue";

const props = defineProps({
  organisation: {
    type: Object,
    required: true,
  },
});

const isModalOpen = ref(false);

const sectorsWrapper = ref(null);
const sectorsMeasurer = ref(null);
const visibleSectorsCount = ref(props.organisation.sectors?.length || 0);

const countriesWrapper = ref(null);
const countriesMeasurer = ref(null);
const visibleCountriesCount = ref(props.organisation.countries?.length || 0);

const GAP = 8;
const MORE_BADGE_WIDTH = 45; 

const updateVisibleCounts = () => {
  if (sectorsWrapper.value && sectorsMeasurer.value) {
    const maxWidth = sectorsWrapper.value.clientWidth;
    const children = Array.from(sectorsMeasurer.value.children);
    let currentWidth = 0;
    let visibleSectors = 0;

    if (children.length > 0) currentWidth += children[0].offsetWidth;

    for (let i = 1; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const isLast = i === children.length - 1;
      const requiredWidth = currentWidth + GAP + childWidth + (!isLast ? GAP + MORE_BADGE_WIDTH : 0);

      if (requiredWidth > maxWidth) break;

      currentWidth += GAP + childWidth;
      visibleSectors++;
    }
    visibleSectorsCount.value = visibleSectors;
  }

  if (countriesWrapper.value && countriesMeasurer.value) {
    const maxWidth = countriesWrapper.value.clientWidth;
    const children = Array.from(countriesMeasurer.value.children);
    let currentWidth = 0;
    let visibleCountries = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const isLast = i === children.length - 1;
      
      const requiredWidth = currentWidth + (i > 0 ? GAP : 0) + childWidth + (!isLast ? GAP + MORE_BADGE_WIDTH : 0);

      if (requiredWidth > maxWidth) break;

      currentWidth += (i > 0 ? GAP : 0) + childWidth;
      visibleCountries++;
    }
    visibleCountriesCount.value = visibleCountries;
  }
};

let resizeObserver;

onMounted(() => {
  resizeObserver = new ResizeObserver(() => {
    updateVisibleCounts();
  });

  if (sectorsWrapper.value) resizeObserver.observe(sectorsWrapper.value);
  if (countriesWrapper.value) resizeObserver.observe(countriesWrapper.value);

  nextTick(() => {
    updateVisibleCounts();
  });
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

const formattedDate = computed(() => {
  if (!props.organisation?.updated_at) return "N/A";
  const date = new Date(props.organisation.updated_at);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
});
</script>